<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import BasicButton from '~/components/form-elements/BasicButton.vue'
import DolyameButton from '~/components/form-elements/DolyameButton.vue'
import QuantityCounter from '~/components/form-elements/QuantityCounter.vue'
import AddProductToList from '~/components/modal-windows/AddProductToList.vue'
import BackInStockNotification from '~/components/modal-windows/BackInStockNotification.vue'
import ShareLink from '~/components/modal-windows/ShareLink.vue'
import DiscountLabel from '~/components/products/DiscountLabel.vue'
import Multiplicity from '~/components/products/Multiplicity.vue'
import ProductImagesGallery from '~/components/products/ProductImagesGallery.vue'
import ProductLabels from '~/components/products/ProductLabels.vue'
import ProductVariations from '~/components/products/ProductVariations.vue'
import RatingComponent from '~/components/products/Rating.vue'
import { useProduct } from '~/composables/product'
import constants from '~/constants'
import helpers from '~/helpers'
import { useAuthStore } from '~/store/auth'
import { useProductsStore } from '~/store/products'
import type { ProductData } from '~/types/products'

const { addProductToCart, toggleIsInWishList, setCartProductQuantity } = useProduct()
const authStore = useAuthStore()
const productsStore = useProductsStore()

const props = defineProps<{
  productData: ProductData
}>()

const brandData = computed(() => props.productData.relationships.brand?.data)
const canBeOrdered = computed(() => !isOutOfStock.value || isOnPreOrder.value)
const categoriesData = computed(() => props.productData.relationships.categories?.data ?? [])
const cashbackInPoints = computed(() => props.productData.meta.cashbackInPoints)
const galleriesData = computed(() => props.productData.relationships.galleries.data)
const heading = computed(() => seoData.value?.h1 ?? name.value)
const imagesData = computed(() => props.productData.relationships.images.data)
const isCertificate = computed(
  () => props.productData.relationships.categories.data.some(
    (x: any): boolean => x.id === constants.categories.certificates.id
  )
)
const isHalal = computed(() => props.productData.meta.isHalal)
const isInWishList = computed(() => props.productData.meta.isInWishList)
const isOnlyAvailableInMoscow = computed(() => props.productData.attributes.isOnlyAvailableInMoscow)
const isOnPreOrder = computed(() => props.productData.meta.isOnPreOrder)
const isOnWorldShoppingDay = computed(() => categoriesData.value.find((x: any) => x.id === '388') !== undefined)
const isOutOfStock = computed(() => props.productData.meta.isOutOfStock)
const multiplicity = computed(() => props.productData.attributes.multiplicity ?? 1)
const name = computed(() => props.productData.attributes.name)
const price = computed(() => props.productData.attributes.price)
const quantity = computed({
  get (): number {
    return productsStore.getCartProductQuantity(props.productData.id) ?? 0
  },
  set (quantity: number): void {
    setCartProductQuantity(props.productData.id, quantity)
  }
})
const rating = computed(() => {
  const { rating } = props.productData.attributes

  return typeof rating === 'number' ? rating : null
})
const seoData = computed(() => props.productData.attributes.seoData)
const variations = computed(() => props.productData.meta.variations ?? [])
const videos = computed(() => props.productData.meta.videos ?? [])
const warning = computed(() => props.productData.attributes.warning)

async function openAddProductToListModalWindow (): Promise<void> {
  await useModal({ component: AddProductToList, attrs: { productId: props.productData.id } }).open()
}

async function openBackInStockNotificationModalWindow (): Promise<void> {
  await useModal({ component: BackInStockNotification, attrs: { productId: props.productData.id } }).open()
}

async function openShareLinkModalWindow (): Promise<void> {
  const link = `${constants.siteUrl}${helpers.getProductPath(props.productData)}`

  await useModal({ component: ShareLink, attrs: { link } }).open()
}
</script>

<template>
  <div class="main-wrapper">
    <div class="row-wrapper bg-white">
      <div class="row justify-between">
        <div class="column-7 tb12 description no-border flex flex-col justify-between">
          <div>
            <div class="mb-4 flex">
              <h1 class="mb-0">
                {{ heading }}
              </h1>

              <div
                v-if="authStore.isUser"
                class="button-list mobile flex cursor-pointer items-center justify-center"
                @click="openAddProductToListModalWindow"
              >
                <SvgIcon name="outlined/playlist-add" />
              </div>
            </div>

            <TaxonomyCarousel v-if="categoriesData.length" :categories-data="categoriesData" class="mb-4" />

            <div class="mb-4">
              <NuxtLink v-if="brandData" :to="helpers.getBrandPath(brandData)">
                {{ brandData.attributes.name }}
              </NuxtLink>
            </div>

            <div class="product-tags desktop mb-4">
              <RatingComponent
                v-if="rating !== null"
                class="rating-area right-offset"
                is-product-page
                :rating="rating"
              />

              <DiscountLabel v-if="canBeOrdered" class="right-offset" is-product-page :product-data="productData" />

              <ProductLabels is-product-page :product-data="productData" />
            </div>

            <Notification v-if="warning" class="mb-6" status="warning">
              {{ warning }}
            </Notification>

            <Notification v-if="isOnlyAvailableInMoscow" class="mb-6" is-small status="warning">
              Доставляется только по&nbsp;Москве.
            </Notification>

            <ProductVariations :variations="variations" />

            <!-- TODO: Refactor -->
            <Notification v-if="false && isOnWorldShoppingDay" class="my-6" status="info">
              Товар участвует в&nbsp;акции&nbsp;11.11: ищите дополнительную скидку в&nbsp;приложении&nbsp;iTAB.

              <a href="/app/" target="_blank">Скачать&nbsp;приложение</a>
            </Notification>
          </div>

          <div>
            <div class="quantity-counter-area flex justify-between">
              <div>
                <PriceWithPromoCode mode="product-page" :product-data="productData" />

                <Cashback
                  v-if="typeof cashbackInPoints === 'number' && cashbackInPoints !== 0"
                  :cashback-in-points="cashbackInPoints"
                />

                <Multiplicity class="mt-2" :multiplicity="multiplicity" />

                <DolyameButton
                  v-if="constants.isDolyameEnabled && !isCertificate"
                  class="mt-4"
                  is-wide-on-mobile
                  :price="price"
                />
              </div>

              <div class="buttons-wrapper flex">
                <template v-if="canBeOrdered">
                  <QuantityCounter
                    v-if="quantity"
                    v-model="quantity"
                    class="quantity-counter"
                    is-wide-on-mobile
                    :min="0"
                    :multiplicity="multiplicity"
                  />
                  <BasicButton
                    v-else
                    color="green"
                    is-small
                    is-wide-on-mobile
                    :title="'В\xA0корзину'"
                    @click="addProductToCart(productData.id, multiplicity)"
                  />
                </template>
                <BasicButton
                  v-else
                  color="body-background"
                  is-small
                  is-wide-on-mobile
                  :title="'Уведомить о\xA0поступлении'"
                  @click="openBackInStockNotificationModalWindow"
                />

                <div
                  v-if="authStore.isUser"
                  class="button-list desktop flex cursor-pointer items-center justify-center"
                  @click="openAddProductToListModalWindow"
                >
                  <SvgIcon name="outlined/playlist-add" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="column-5 tb12 with-margin-bottom no-border">
          <div class="image-area">
            <ProductImagesGallery
              :class="{ 'grayscale': helpers.isProductImageBlackAndWhite(productData) }"
              :galleries-data="galleriesData"
              :images-data="imagesData"
              :product-name="name"
              :videos="videos"
            >
              <template #overlay>
                <div
                  class="overlay-icon wish-list absolute flex cursor-pointer items-center justify-center bg-white"
                  @click="toggleIsInWishList(productData)"
                >
                  <SvgIcon
                    :color="isInWishList ? 'red' : 'light-gray'"
                    :name="isInWishList ? 'outlined/favorite' : 'outlined/favorite-border'"
                  />
                </div>

                <div
                  class="overlay-icon share absolute flex cursor-pointer items-center justify-center bg-white"
                  @click="openShareLinkModalWindow"
                >
                  <SvgIcon name="outlined/ios-share" />
                </div>

                <RatingComponent
                  v-if="rating !== null"
                  class="overlay-rating-mobile rating-area"
                  is-product-page
                  :rating="rating"
                />

                <div class="overlay-product-tags-mobile">
                  <DiscountLabel v-if="canBeOrdered" is-product-page :product-data="productData" />
                  <ProductLabels class="label-mobile" is-product-page :product-data="productData" />
                </div>

                <SvgIcon
                  v-if="isHalal"
                  class="product-header__halal-icon flex items-center justify-center text-main-green"
                  name="labels/halal"
                />
              </template>
            </ProductImagesGallery>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'assets/css/variables';

.main-wrapper {
  margin: 0 auto;
  max-width: 1440px;
  padding: 0 40px;

  @media screen and (max-width: 1020px) {
    padding: 0;
  }
}

.button-list {
  background: #f6f6f6;
  border-radius: 12px;
  flex-shrink: 0;
  font-size: 24px;
  height: 40px;
  margin-left: 16px;
  width: 40px;

  &:hover {
    color: variables.$main-green-color;
  }

  &.desktop {
    @media screen and (max-width: 1020px) {
      display: none;
    }
  }

  &.mobile {
    display: none;

    @media screen and (max-width: 1020px) {
      display: flex;
    }
  }

  > svg {
    transition: color .25s ease-in-out;
  }
}

.row-wrapper {
  border-radius: 12px;
  padding: 24px 0;

  @media screen and (max-width: 1020px) {
    border-radius: 0;
  }

  @media screen and (max-width: 600px) {
    padding: 16px 0;
  }

  > .row {
    @media screen and (max-width: 1020px) {
      margin: 0 auto;
      width: 100%;
    }
  }
}

.no-border {
  @media screen and (max-width: 1020px) {
    border: 0;
  }
}

.description {
  padding-left: 24px;

  @media screen and (max-width: 1020px) {
    margin-top: 16px;
    order: 2;
    padding: 0 8px;
  }
}

.product-tags {
  &.desktop {
    display: flex;
    flex-direction: row;

    @media screen and (max-width: 1020px) {
      display: none;
    }
  }

  > .right-offset {
    margin-right: 8px;
  }
}

.quantity-counter-area {
  @media screen and (max-width: 480px) {
    display: block;
  }

  .buttons-wrapper {
    @media screen and (max-width: 480px) {
      margin-top: 16px;
      justify-content: flex-end;
    }
  }
}

.overlay-icon {
  border-radius: 50%;
  border: 1px solid variables.$main-light-gray-color;
  height: 40px;
  width: 40px;

  &.share,
  &.wish-list {
    z-index: 2;

    > svg {
      font-size: 24px;
      transition: color .25s ease-in-out;
    }
  }

  &.share {
    left: 64px;
    top: 8px;

    @media screen and (max-width: 1020px) {
      left: unset;
      right: 16px; // 64px
    }

    &:hover {
      color: variables.$main-blue-color;
    }
  }

  &.wish-list {
    left: 8px;
    top: 8px;

    @media screen and (max-width: 1020px) {
      left: unset;
      right: 64px;
    }

    &:hover {
      color: variables.$main-red-color;
    }
  }
}

.overlay-rating-mobile {
  bottom: 8px;
  display: none;
  left: 16px;
  position: absolute;
  z-index: 2;

  @media screen and (max-width: 1020px) {
    display: flex;
  }
}

.overlay-product-tags-mobile {
  display: none;
  position: absolute;
  z-index: 2;

  & > .label-mobile {
    margin-left: 8px;
  }

  @media screen and (max-width: 1020px) {
    display: flex;
    flex-direction: column;
    left: 16px;
    top: 8px;
  }
}

.image-area {
  margin-right: 24px;
  position: relative;

  @media screen and (max-width: 1020px) {
    margin-right: 0;
  }
}

.product-header__halal-icon {
  box-sizing: border-box;
  font-size: 42px;
  position: absolute;
  right: 8px;
  top: 8px;
  z-index: 2;

  @media screen and (max-width: 1020px) {
    right: 16px;
    top: 58px;
  }
}
</style>
